import moment from "moment";
import { BANNER_TYPE, NAVIGATION_TYPE, SELLER_SEGMENT_TYPE, SQUARE_IMAGE_SCREENS } from "./constants";

export const RFC3339_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";
export const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";
export const DISPLAY_DATETIME_FORMAT = "DD/MM/YYYY HH:mm";

export function formatRFC3339(momentDate) {
  if (!momentDate) return null;
  return moment(momentDate).format(RFC3339_FORMAT);
}

export function parseMoment(dateStr) {
  if (!dateStr) return null;
  const m = moment(dateStr);
  return m.isValid() ? m : null;
}

export function formatDateRangeDisplay(startTime, endTime) {
  if (!startTime && !endTime) return "—";
  const start = startTime ? moment(startTime).format(DISPLAY_DATE_FORMAT) : "";
  const end = endTime ? moment(endTime).format(DISPLAY_DATE_FORMAT) : "";
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

export function formatDateTimeRangeDisplay(startTime, endTime) {
  if (!startTime && !endTime) return "—";
  const start = startTime ? moment(startTime).format(DISPLAY_DATETIME_FORMAT) : "";
  const end = endTime ? moment(endTime).format(DISPLAY_DATETIME_FORMAT) : "";
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

/** Kiểm tra xem mảng screens có màn hình vuông (WELCOME, CHAT, WARRANTY_LOOKUP) không */
export function hasSquareScreen(screens = []) {
  return screens.some((s) => SQUARE_IMAGE_SCREENS.includes(s));
}

/** Kiểm tra xem mảng screens có màn hình ngang không (mọi màn trừ 3 màn vuông trên) */
export function hasLandscapeScreen(screens = []) {
  return screens.some((s) => !SQUARE_IMAGE_SCREENS.includes(s));
}

/** Lấy tên hiển thị của các màn hình */
export function getScreenDisplayNames(screens = [], screenLabels = {}) {
  if (!screens || screens.length === 0) return "—";
  return screens
    .map((s) => {
      const code = typeof s === "object" ? s.screen : s;
      return screenLabels[code] || code;
    })
    .join(", ");
}

/** Tóm tắt link điều hướng cho table */
export function formatNavigationSummary(navigation) {
  if (!navigation || navigation.type === NAVIGATION_TYPE.NONE) {
    return "Không điều hướng";
  }
  if (navigation.targetName) {
    return navigation.targetName;
  }
  if (navigation.url) {
    return navigation.url;
  }
  if (navigation.targetID) {
    return `#${navigation.targetID}`;
  }
  return "Có điều hướng";
}

/**
 * Validate form tạo mới / cập nhật Banner theo rule của backend spec
 */
export function validateBannerForm(values, type, source) {
  const errors = {};

  if (!values.name || !values.name.trim()) {
    errors.name = "Vui lòng nhập tên công cụ truyền thông";
  }

  // Với banner PROMOTION, BE chỉ nhận imageURL, squareImageURL và screens;
  // Các field khác không cần validate.
  if (source === "PROMOTION") {
    if (type === BANNER_TYPE.BANNER) {
      const screens = values.screens || [];
      if (screens.length === 0) {
        errors.screens = "Vui lòng chọn ít nhất một màn hình hiển thị";
      }
      if (hasLandscapeScreen(screens) && !values.imageURL) {
        errors.imageURL = "Vui lòng tải ảnh banner 1800x600 cho màn hình hiển thị dạng ngang";
      }
      if (hasSquareScreen(screens) && !values.squareImageURL) {
        errors.squareImageURL =
          "Vui lòng tải ảnh banner 1500x1500 cho màn hình Welcome, Tin nhắn hoặc Tra cứu bảo hành";
      }
    }
    return errors;
  }

  // Thời gian
  if (!values.timeRange || values.timeRange.length < 2 || !values.timeRange[0] || !values.timeRange[1]) {
    errors.timeRange = "Vui lòng chọn thời gian diễn ra";
  } else {
    const [start, end] = values.timeRange;
    if (end.isSameOrBefore(start)) {
      errors.timeRange = "Thời gian kết thúc phải sau thời gian bắt đầu";
    } else if (end.isBefore(moment())) {
      errors.timeRange = "Thời gian kết thúc không được nhỏ hơn thời gian hiện tại";
    }
  }

  // Khách hàng mục tiêu
  if (!values.sellerSegmentType) {
    errors.sellerSegmentType = "Vui lòng chọn khách hàng mục tiêu";
  } else if (
    values.sellerSegmentType === SELLER_SEGMENT_TYPE.NOT_PURCHASED_PRODUCT &&
    (!values.applicableProductIDs || values.applicableProductIDs.length === 0)
  ) {
    errors.applicableProductIDs = "Vui lòng chọn sản phẩm áp dụng";
  }

  // Màn hình hiển thị
  const screens = values.screens || [];
  if (screens.length === 0) {
    errors.screens = "Vui lòng chọn ít nhất một màn hình hiển thị";
  }

  // Điều hướng
  const navType = values.navigationType || NAVIGATION_TYPE.NONE;
  if (
    [
      NAVIGATION_TYPE.PROMOTION_CAMPAIGN,
      NAVIGATION_TYPE.PRODUCT_DETAIL,
      NAVIGATION_TYPE.CATEGORY,
      NAVIGATION_TYPE.BRAND,
    ].includes(navType)
  ) {
    if (!values.navigationTargetID) {
      errors.navigationTargetID = "Vui lòng chọn đích điều hướng";
    }
  } else if (navType === NAVIGATION_TYPE.LANDING_PAGE) {
    if (!values.navigationURL || !values.navigationURL.trim()) {
      errors.navigationURL = "Vui lòng nhập đường dẫn điều hướng";
    } else if (!values.navigationURL.startsWith("https://")) {
      errors.navigationURL = "Đường dẫn landing page phải bắt đầu bằng https://";
    }
  } else if (navType === NAVIGATION_TYPE.APP_FEATURE) {
    if (!values.navigationURL || !values.navigationURL.trim()) {
      errors.navigationURL = "Vui lòng nhập đường dẫn điều hướng";
    }
  }

  // Rule theo loại công cụ
  if (type === BANNER_TYPE.BANNER) {
    if (hasLandscapeScreen(screens) && !values.imageURL) {
      errors.imageURL = "Vui lòng tải ảnh banner 1800x600 cho màn hình hiển thị dạng ngang";
    }
    if (hasSquareScreen(screens) && !values.squareImageURL) {
      errors.squareImageURL = "Vui lòng tải ảnh banner 1500x1500 cho màn hình Welcome, Tin nhắn hoặc Tra cứu bảo hành";
    }
  } else if (type === BANNER_TYPE.POPUP) {
    if (!values.imageURL) {
      errors.imageURL = "Vui lòng tải ảnh popup 600x600";
    }
    if (!values.popupTriggerType) {
      errors.popupTriggerType = "Vui lòng chọn thời điểm kích hoạt";
    }
    if (values.displayDurationType === "LIMITED") {
      const dur = Number(values.displayDurationSeconds);
      if (
        values.displayDurationSeconds == null ||
        values.displayDurationSeconds === "" ||
        isNaN(dur) ||
        dur < 3 ||
        dur > 60
      ) {
        errors.displayDurationSeconds = "Thời lượng hiển thị phải từ 3 đến 60 giây.";
      }
    }
    if (values.maxImpressions == null || values.maxImpressions <= 0) {
      errors.maxImpressions = "Vui lòng nhập tổng số lần hiển thị lớn hơn 0";
    }
    if (
      values.stopAfterClicks == null ||
      values.stopAfterClicks === "" ||
      isNaN(Number(values.stopAfterClicks)) ||
      Number(values.stopAfterClicks) < 1 ||
      Number(values.stopAfterClicks) > 5
    ) {
      errors.stopAfterClicks = "Số lần click phải từ 1 đến 5.";
    }
    if (values.frequencyCount == null || values.frequencyCount <= 0) {
      errors.frequencyCount = "Vui lòng nhập tần suất hiển thị";
    } else if (
      values.maxImpressions != null &&
      values.maxImpressions > 0 &&
      Number(values.frequencyCount) > Number(values.maxImpressions)
    ) {
      errors.frequencyCount = "Số lần trong chu kỳ không được lớn hơn tổng số lần hiển thị.";
    }
  } else if (type === BANNER_TYPE.ANNOUNCEMENT_BAR) {
    if (!values.announcementBarPosition) {
      errors.announcementBarPosition = "Vui lòng chọn vị trí hiển thị";
    }
    const displayType = values.announcementBarDisplayType || "TEXT";
    if (displayType === "TEXT") {
      if (!values.announcementBarMessage || !values.announcementBarMessage.trim()) {
        errors.announcementBarMessage = "Vui lòng nhập nội dung thông báo";
      }
    } else if (displayType === "IMAGE") {
      if (!values.imageURL) {
        errors.imageURL = "Vui lòng tải ảnh announcement bar (1125x90)";
      }
    }
  }

  return errors;
}

/** Serialize form state for dirty checking */
export function serializeBannerForm(values) {
  if (!values) return "";
  return JSON.stringify({
    name: values.name,
    timeRange: values.timeRange ? [values.timeRange[0]?.toISOString(), values.timeRange[1]?.toISOString()] : null,
    sellerSegmentType: values.sellerSegmentType,
    applicableProductIDs: values.applicableProductIDs || [],
    screens: values.screens || [],
    imageURL: values.imageURL,
    squareImageURL: values.squareImageURL,
    navigationType: values.navigationType,
    navigationTargetID: values.navigationTargetID,
    navigationURL: values.navigationURL,
    popup: values.popup,
    announcementBar: values.announcementBar,
  });
}

/**
 * Validate dung lượng file ảnh theo quy tắc:
 * - JPG, JPEG, PNG: dung lượng ≤ 1MB
 * - WEBP, GIF: dung lượng ≤ 3MB
 * Vượt quá dung lượng → Cảnh báo: "Dung lượng ảnh vượt quá [X]MB."
 */
export function validateBannerImageFile(file) {
  if (!file) return { valid: false, message: "Vui lòng chọn file ảnh" };

  const fileName = file.name || "";
  const ext = fileName.includes(".") ? fileName.slice(fileName.lastIndexOf(".") + 1).toLowerCase() : "";
  const mimeType = (file.type || "").toLowerCase();

  let maxMB = 1;
  if (["webp", "gif"].includes(ext) || mimeType === "image/webp" || mimeType === "image/gif") {
    maxMB = 3;
  } else {
    // Mặc định hoặc JPG, JPEG, PNG
    maxMB = 1;
  }

  const maxBytes = maxMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return {
      valid: false,
      message: `Dung lượng ảnh vượt quá ${maxMB}MB.`,
      maxMB,
    };
  }

  return { valid: true, maxMB };
}

/**
 * Lấy kích thước pixel (width x height) của file ảnh hoặc URL ảnh
 * @param {File|Blob|string} source
 * @returns {Promise<{ width: number, height: number }>}
 */
export function getImageDimensions(source) {
  return new Promise((resolve, reject) => {
    if (!source) return reject(new Error("Nguồn ảnh không hợp lệ"));
    if (typeof window === "undefined" || typeof Image === "undefined") {
      return resolve({ width: 0, height: 0 });
    }
    try {
      const img = new Image();
      let objectUrl = "";
      const isFile = typeof File !== "undefined" && source instanceof File;
      const isBlob = typeof Blob !== "undefined" && source instanceof Blob;

      const cleanup = () => {
        if ((isFile || isBlob) && objectUrl && window.URL && typeof window.URL.revokeObjectURL === "function") {
          try {
            window.URL.revokeObjectURL(objectUrl);
          } catch (e) {
            // ignore
          }
        }
      };

      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        cleanup();
        resolve({ width, height });
      };

      img.onerror = (err) => {
        cleanup();
        reject(err || new Error("Không thể đọc ảnh"));
      };

      if (isFile || isBlob) {
        if (window.URL && typeof window.URL.createObjectURL === "function") {
          objectUrl = window.URL.createObjectURL(source);
          img.src = objectUrl;
        } else if (typeof FileReader !== "undefined") {
          const reader = new FileReader();
          reader.onload = (e) => {
            img.src = e.target.result;
          };
          reader.onerror = (err) => reject(err || new Error("Không thể đọc file"));
          reader.readAsDataURL(source);
        } else {
          resolve({ width: 0, height: 0 });
        }
      } else if (typeof source === "string") {
        img.src = source;
        if (img.complete && img.naturalWidth > 0) {
          const width = img.naturalWidth || img.width;
          const height = img.naturalHeight || img.height;
          cleanup();
          resolve({ width, height });
        }
      } else {
        reject(new Error("Nguồn ảnh không hợp lệ"));
      }
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Validate kích thước pixel của ảnh
 * @param {{ width: number, height: number }} dimensions
 * @param {number} expectedWidth
 * @param {number} expectedHeight
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateImageDimensions(dimensions, expectedWidth, expectedHeight) {
  if (!dimensions || !expectedWidth || !expectedHeight) {
    return { valid: true };
  }
  const { width, height } = dimensions;
  if (width !== expectedWidth || height !== expectedHeight) {
    return {
      valid: false,
      message: `Ảnh phải có kích thước ${expectedWidth} x ${expectedHeight} px.`,
    };
  }
  return { valid: true };
}
